/**
 * Settings — Lightweight orchestrator.
 *
 * ARCHITECTURE RULE (permanent):
 *   - ALL state and logic lives here.
 *   - Each section is rendered by a dedicated component in ./settings/.
 *   - To add a new section: add it to settingsConfig.js, create the component, render it here.
 *   - NEVER rewrite this file from scratch. Only extend.
 */
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useCurrency } from '../context/CurrencyContext';
// Architecture guard config
import { REQUIRED_SECTION_KEYS } from '../config/settingsConfig';
// Section components — each owns its own JSX, receives props from here
import BillingSettings from './settings/BillingSettings';
import BusinessSettings from './settings/BusinessSettings';
import OvertimeSettings from './settings/OvertimeSettings';
import AISettings from './settings/AISettings';
// eslint-disable-next-line no-unused-vars -- kept dormant for legacy SendPulse tenants; tenant UI now uses Resend
import SendPulseSettings from './settings/SendPulseSettings';
import ResendSettings from './settings/ResendSettings';
import LocationsSettings from './settings/LocationsSettings';
import UpgradeSettings from './settings/UpgradeSettings';
import PaymentAdminSettings from './settings/PaymentAdminSettings';
import MCPConnectionsSettings from './settings/MCPConnectionsSettings';
import { CanadianEmployerInfoSection } from '../components/canadian/CanadianEmployerInfoSection';
import { Layout } from '../components/Layout';
import { restaurantGroupAPI, restaurantAPI, stripeAPI, authAPI, pricingAPI, aiAPI, adminSettingsAPI } from '../lib/api';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const DEFAULT_PLANS = {
  single: {
    name: 'Single Location',
    monthlyPrice: 59,
    yearlyPrice: 348,
    maxRestaurants: 1,
  },
  multi: {
    name: 'Multi-Location',
    monthlyPrice: 119,
    yearlyPrice: 708,
    maxRestaurants: -1,
  },
};

/** Returns a safe absolute image URL (used by openEditDialog which runs in this file). */
function resolveImageUrl(url) {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${process.env.REACT_APP_BACKEND_URL}${url}`;
}

export default function Settings() {
  const { user, updateUser } = useAuth();
  const { reportCurrency, saveCurrency } = useCurrency();
  const [loading, setLoading] = useState(true);
  const [restaurantGroup, setRestaurantGroup] = useState(null);
  const [restaurants, setRestaurants] = useState([]);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [locationName, setLocationName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingLocation, setDeletingLocation] = useState(null);
  const [deletePinInput, setDeletePinInput] = useState('');
  const [deletePinError, setDeletePinError] = useState('');
  const [PLANS, setPLANS] = useState(DEFAULT_PLANS);
  const [editForm, setEditForm] = useState({
    location_name: '',
    address: '',
    phone: '',
    email: '',
    labor_target_percent: '',
    weekly_labor_budget: '',
    budget_type: 'manual',
    weekly_revenue_estimate: '',
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [iconFile, setIconFile] = useState(null);
  const [iconPreview, setIconPreview] = useState(null);
  const fileInputRef = useRef(null);
  const iconInputRef = useRef(null);
  const newHolidayNameRef = useRef(null);
  const [newHolidayIdx, setNewHolidayIdx] = useState(null);

  // AI Configuration state
  const [aiKeyInput, setAiKeyInput] = useState('');
  const [aiKeyVisible, setAiKeyVisible] = useState(false);
  const [aiEnabled, setAiEnabled] = useState(false);
  const [aiProvider, setAiProvider] = useState('gemini');
  const [aiStatus, setAiStatus] = useState('unknown'); // 'unknown' | 'not_configured' | 'connected' | 'invalid_key' | 'rate_limited' | 'error'
  const [aiTestDetail, setAiTestDetail] = useState('');
  const [aiSaving, setAiSaving] = useState(false);
  const [aiTesting, setAiTesting] = useState(false);

  // Payroll settings state
  const [payrollSettings, setPayrollSettings] = useState({
    overtime_after_hours: 8,
    overtime_multiplier: 1.25,
    holiday_dates: [],
    break_rule: { enabled: true, hours_per_block: 4, minutes_per_block: 30 },
    rplh_target: '',
  });
  const [payrollSaving, setPayrollSaving] = useState(false);

  // Tenant branding (logo on payslips + emails)
  const [tenantLogoUrl, setTenantLogoUrl] = useState('');
  const [tenantLogoSaving, setTenantLogoSaving] = useState(false);

  // Stripe config state (super_admin only)
  const [stripeConfig, setStripeConfig] = useState({ stripe_publishable_key: '', stripe_secret_key: '', stripe_webhook_secret: '' });
  const [stripeMasked, setStripeMasked] = useState({ stripe_secret_key_masked: null, stripe_webhook_secret_masked: null, stripe_publishable_key: null });
  const [stripeSaving, setStripeSaving] = useState(false);

  // Xendit + processor state (super_admin only)
  const [xenditConfig, setXenditConfig] = useState({ xendit_secret_key: '', xendit_webhook_token: '' });
  const [xenditMasked, setXenditMasked] = useState({ xendit_secret_key_masked: null, xendit_webhook_token_masked: null });
  const [xenditSaving, setXenditSaving] = useState(false);
  const [activeProcessor, setActiveProcessor] = useState('stripe');
  const [processorSaving, setProcessorSaving] = useState(false);
  const [processorSwitchBanner, setProcessorSwitchBanner] = useState(null);

  // ARCHITECTURE GUARD — runs only in development.
  // Warns if any required Settings section is missing from the DOM.
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      const timer = setTimeout(() => {
        REQUIRED_SECTION_KEYS.forEach((key) => {
          const el = document.querySelector(`[data-settings-section="${key}"]`);
          if (!el) {
            console.warn(
              `[SettingsGuard] Section "${key}" is missing from the Settings page. ` +
              `Check settingsConfig.js and restore the section in Settings.js.`
            );
          }
        });
      }, 500);
      return () => clearTimeout(timer);
    }
  }, []);

  // Fetch pricing from backend (single source of truth)
  useEffect(() => {
    pricingAPI.getPricing()
      .then(({ data }) => {
        const backendPlans = {};
        Object.keys(data.plans).forEach((key) => {
          const p = data.plans[key];
          backendPlans[key] = {
            name: p.name,
            monthlyPrice: p.monthly_price,
            yearlyPrice: p.yearly_price,
            maxRestaurants: p.max_locations,
          };
        });
        setPLANS(backendPlans);
      })
      .catch(() => {
        // Keep default plans on error
      });
  }, []);

  // Copy location PIN
  const copyLocationPin = (pin) => {
    navigator.clipboard.writeText(pin);
    toast.success('Location PIN copied to clipboard');
  };

  // Delete location with PIN validation
  const handleDeleteLocation = async () => {
    if (!deletingLocation || deletePinInput.length !== 6) return;
    setSubmitting(true);
    setDeletePinError('');
    try {
      await restaurantAPI.delete(deletingLocation.id, deletePinInput);
      toast.success(`"${deletingLocation.location_name}" has been deleted`);
      setDeleteDialogOpen(false);
      setDeletingLocation(null);
      setDeletePinInput('');
      fetchData();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to delete location';
      if (msg.includes('Incorrect PIN')) {
        setDeletePinError('Incorrect PIN. Deletion cancelled.');
      } else {
        toast.error(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [user]);

  const fetchData = async () => {
    try {
      const [groupRes, restaurantsRes] = await Promise.all([
        restaurantGroupAPI.getMine(),
        restaurantAPI.getAll(),
      ]);

      setRestaurantGroup(groupRes.data);
      setRestaurants(restaurantsRes.data);

      // Fetch AI configuration status (owner + super_admin)
      if (['owner', 'super_admin'].includes(user?.role)) {
        try {
          const { tenantAPI: tAPI } = await import('../lib/api');
          const tRes = await tAPI.getSettings();
          setAiEnabled(!!tRes.data.ai_enabled);
          setAiProvider(tRes.data.ai_provider || 'gemini');
          // "Connected" only if backend confirms last test succeeded
          if (tRes.data.ai_connection_valid) {
            setAiStatus('connected');
          } else if (tRes.data.ai_configured) {
            setAiStatus('configured');
          } else {
            setAiStatus('not_configured');
          }
          // Load payroll settings
          setPayrollSettings({
            overtime_after_hours: tRes.data.overtime_after_hours ?? 8,
            overtime_multiplier: tRes.data.overtime_multiplier ?? 1.25,
            holiday_dates: tRes.data.holiday_dates ?? [],
            break_rule: tRes.data.break_rule ?? { enabled: true, hours_per_block: 4, minutes_per_block: 30 },
            rplh_target: tRes.data.rplh_target != null ? String(tRes.data.rplh_target) : '',
          });
          // Load branding (logo URL)
          setTenantLogoUrl(tRes.data.logo_url || '');
        } catch (_) {
          // Non-critical
        }
      }

      // Fetch subscription status
      try {
        const subRes = await stripeAPI.getSubscriptionStatus();
        setSubscriptionStatus(subRes.data);
      } catch (e) {
        // User may not have subscription yet
        console.log('No subscription status available');
      }

      // Fetch payment config (super_admin only)
      if (user?.role === 'super_admin') {
        try {
          const { data } = await adminSettingsAPI.getPaymentConfig();
          setStripeMasked(data);
          setXenditMasked(data);
          setActiveProcessor(data.payment_processor || 'stripe');
          if (data.auto_corrected) {
            const from = data.auto_corrected_from || 'unknown';
            const to = data.auto_corrected_to || 'stripe';
            setProcessorSwitchBanner(
              `Payment processor was automatically switched from ${from.charAt(0).toUpperCase() + from.slice(1)} to ${to.charAt(0).toUpperCase() + to.slice(1)} because ${from} credentials are missing or incomplete.`
            );
          }
        } catch (_) { /* non-critical */ }
      }
    } catch (error) {
      console.error('Error fetching settings data:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveAiSettings = async () => {
    setAiSaving(true);
    try {
      await aiAPI.saveSettings({ ai_provider: aiProvider, ai_api_key: aiKeyInput || undefined, ai_enabled: aiEnabled });
      toast.success('AI settings saved');
      setAiKeyInput('');
      setAiStatus(aiKeyInput ? 'configured' : 'not_configured');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save AI settings');
    } finally {
      setAiSaving(false);
    }
  };

  const testAiConnection = async () => {
    setAiTesting(true);
    setAiStatus('unknown');
    setAiTestDetail('');
    try {
      const res = await aiAPI.test();
      setAiStatus(res.data.status);
      if (res.data.detail) setAiTestDetail(res.data.detail);
    } catch (err) {
      setAiStatus('error');
      setAiTestDetail(err.response?.data?.detail || 'Could not reach the server. Check your network.');
    } finally {
      setAiTesting(false);
    }
  };

  // Payroll settings handlers
  const savePayrollSettings = async () => {
    setPayrollSaving(true);
    try {
      const rplhTarget = payrollSettings.rplh_target !== '' && payrollSettings.rplh_target != null
        ? parseFloat(payrollSettings.rplh_target)
        : null;
      if (rplhTarget !== null && (isNaN(rplhTarget) || rplhTarget < 0)) {
        toast.error('RPLH target must be a positive number');
        setPayrollSaving(false);
        return;
      }
      await restaurantGroupAPI.updateSettings({ ...payrollSettings, rplh_target: rplhTarget });
      toast.success('Payroll settings saved');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save payroll settings');
    } finally {
      setPayrollSaving(false);
    }
  };

  const saveTenantLogo = async () => {
    const trimmed = (tenantLogoUrl || '').trim();
    if (trimmed && !/^https?:\/\//i.test(trimmed)) {
      toast.error('Logo URL must start with https:// (or http://)');
      return;
    }
    setTenantLogoSaving(true);
    try {
      await restaurantGroupAPI.updateSettings({ logo_url: trimmed });
      toast.success(trimmed ? 'Logo saved — payslips & emails will use it' : 'Logo removed');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save logo');
    } finally {
      setTenantLogoSaving(false);
    }
  };

  const saveStripeConfig = async () => {
    if (!stripeConfig.stripe_publishable_key && !stripeConfig.stripe_secret_key && !stripeConfig.stripe_webhook_secret) {
      toast.error('Enter at least one key to save');
      return;
    }
    setStripeSaving(true);
    try {
      await adminSettingsAPI.updateStripeConfig({
        stripe_publishable_key: stripeConfig.stripe_publishable_key || undefined,
        stripe_secret_key: stripeConfig.stripe_secret_key || undefined,
        stripe_webhook_secret: stripeConfig.stripe_webhook_secret || undefined,
      });
      toast.success('Stripe configuration saved');
      setStripeConfig({ stripe_publishable_key: '', stripe_secret_key: '', stripe_webhook_secret: '' });
      const { data } = await adminSettingsAPI.getPaymentConfig();
      setStripeMasked(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save Stripe config');
    } finally {
      setStripeSaving(false);
    }
  };

  const saveXenditConfig = async () => {
    if (!xenditConfig.xendit_secret_key && !xenditConfig.xendit_webhook_token) {
      toast.error('Enter at least one Xendit field to save');
      return;
    }
    setXenditSaving(true);
    try {
      await adminSettingsAPI.updateXenditConfig({
        xendit_secret_key: xenditConfig.xendit_secret_key || undefined,
        xendit_webhook_token: xenditConfig.xendit_webhook_token || undefined,
      });
      toast.success('Xendit configuration saved');
      setXenditConfig({ xendit_secret_key: '', xendit_webhook_token: '' });
      const { data } = await adminSettingsAPI.getPaymentConfig();
      setXenditMasked(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save Xendit config');
    } finally {
      setXenditSaving(false);
    }
  };

  const switchPaymentProcessor = async (processor) => {
    setProcessorSaving(true);
    try {
      await adminSettingsAPI.setPaymentProcessor(processor);
      setActiveProcessor(processor);
      setProcessorSwitchBanner(null);
      toast.success(`Active processor switched to ${processor === 'stripe' ? 'Stripe' : 'Xendit'}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to switch processor');
    } finally {
      setProcessorSaving(false);
    }
  };

  const addHolidayDate = () => {
    setPayrollSettings(prev => {
      const nextIdx = prev.holiday_dates.length;
      setNewHolidayIdx(nextIdx);
      return {
        ...prev,
        holiday_dates: [...prev.holiday_dates, { name: '', date: '', holiday_type: 'regular' }],
      };
    });
  };

  // Auto-scroll + auto-focus when a new holiday row is added
  useEffect(() => {
    if (newHolidayIdx === null) return;
    const timer = setTimeout(() => {
      if (newHolidayNameRef.current) {
        // Input ref gives us the <input> element — scroll its closest row parent
        const el = newHolidayNameRef.current;
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.focus();
      }
      // Remove highlight after 2 s
      setTimeout(() => setNewHolidayIdx(null), 2000);
    }, 60);
    return () => clearTimeout(timer);
  }, [newHolidayIdx]);

  const removeHolidayDate = (idx) => {
    setPayrollSettings(prev => ({
      ...prev,
      holiday_dates: prev.holiday_dates.filter((_, i) => i !== idx),
    }));
  };

  const updateHolidayDate = (idx, field, value) => {
    setPayrollSettings(prev => {
      const updated = [...prev.holiday_dates];
      updated[idx] = { ...updated[idx], [field]: value };
      return { ...prev, holiday_dates: updated };
    });
  };

  const handleAddLocation = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await restaurantAPI.create({ location_name: locationName });
      
      // Refresh token to get updated user data
      const { data: refreshData } = await authAPI.refreshToken();
      localStorage.setItem('token', refreshData.token);
      localStorage.setItem('user', JSON.stringify(refreshData.user));
      updateUser(refreshData.user);
      
      toast.success('Location added!');
      setDialogOpen(false);
      setLocationName('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add location');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubscribe = async () => {
    try {
      const response = await stripeAPI.createCheckout({
        plan_type: restaurantGroup.plan_type,
        billing_cycle: billingCycle,
      });

      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
    }
  };

  const handleUpgrade = async () => {
    setSubmitting(true);
    try {
      // Call the upgrade plan API
      const { data } = await stripeAPI.upgradePlan({
        new_plan_type: 'multi',
        billing_cycle: subscriptionStatus?.billing_cycle || 'monthly',
      });

      toast.success(data.message || 'Successfully upgraded to Multi-Location plan!');
      setUpgradeDialogOpen(false);
      
      // Refresh data to show new plan
      fetchData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to upgrade plan';
      toast.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleChangeBillingCycle = async (newCycle) => {
    setSubmitting(true);
    try {
      const { data } = await stripeAPI.changeBillingCycle(newCycle);
      toast.success(data.message || `Billing cycle changed to ${newCycle}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change billing cycle');
    } finally {
      setSubmitting(false);
    }
  };

  const openEditDialog = (restaurant) => {
    setEditingLocation(restaurant);
    setEditForm({
      location_name: restaurant.location_name || '',
      address: restaurant.address || '',
      phone: restaurant.phone || '',
      email: restaurant.email || '',
      labor_target_percent: restaurant.labor_target_percent ?? '',
      weekly_labor_budget: restaurant.weekly_labor_budget ?? '',
      budget_type: restaurant.budget_type || 'manual',
      weekly_revenue_estimate: restaurant.weekly_revenue_estimate ?? '',
    });
    setLogoPreview(resolveImageUrl(restaurant.logo_url));
    setIconPreview(resolveImageUrl(restaurant.icon_url));
    setLogoFile(null);
    setIconFile(null);
    setEditDialogOpen(true);
  };

  const handleLogoSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Allowed: JPEG, PNG, GIF, WEBP');
      return;
    }

    // Validate file size (2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 2MB');
      return;
    }

    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const handleIconSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Allowed: JPEG, PNG, GIF, WEBP');
      return;
    }

    // Validate file size (1MB for icons)
    if (file.size > 1 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 1MB for icons');
      return;
    }

    setIconFile(file);
    setIconPreview(URL.createObjectURL(file));
  };

  const handleDeleteLogo = async () => {
    if (!editingLocation) return;
    
    setSubmitting(true);
    try {
      await restaurantAPI.deleteLogo(editingLocation.id);
      toast.success('Logo deleted');
      setLogoPreview(null);
      setLogoFile(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete logo');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteIcon = async () => {
    if (!editingLocation) return;
    
    setSubmitting(true);
    try {
      await restaurantAPI.deleteIcon(editingLocation.id);
      toast.success('Icon deleted');
      setIconPreview(null);
      setIconFile(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete icon');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditLocation = async (e) => {
    e.preventDefault();
    if (!editingLocation) return;

    setSubmitting(true);
    try {
      // Update location details
      const updateData = {};
      if (editForm.location_name !== editingLocation.location_name) {
        updateData.location_name = editForm.location_name;
      }
      if (editForm.address !== (editingLocation.address || '')) {
        updateData.address = editForm.address;
      }
      if (editForm.phone !== (editingLocation.phone || '')) {
        updateData.phone = editForm.phone;
      }
      if (editForm.email !== (editingLocation.email || '')) {
        updateData.email = editForm.email;
      }

      // Budget fields
      const ltp = editForm.labor_target_percent === '' ? null : parseFloat(editForm.labor_target_percent);
      if (ltp !== (editingLocation.labor_target_percent ?? null)) {
        updateData.labor_target_percent = ltp;
      }
      const wlb = editForm.weekly_labor_budget === '' ? null : parseFloat(editForm.weekly_labor_budget);
      if (wlb !== (editingLocation.weekly_labor_budget ?? null)) {
        updateData.weekly_labor_budget = wlb;
      }
      if (editForm.budget_type !== (editingLocation.budget_type || 'manual')) {
        updateData.budget_type = editForm.budget_type;
      }
      const wre = editForm.weekly_revenue_estimate === '' ? null : parseFloat(editForm.weekly_revenue_estimate);
      if (wre !== (editingLocation.weekly_revenue_estimate ?? null)) {
        updateData.weekly_revenue_estimate = wre;
      }

      if (Object.keys(updateData).length > 0) {
        await restaurantAPI.update(editingLocation.id, updateData);
      }

      // Upload logo if selected
      if (logoFile) {
        await restaurantAPI.uploadLogo(editingLocation.id, logoFile);
      }

      // Upload icon if selected
      if (iconFile) {
        await restaurantAPI.uploadIcon(editingLocation.id, iconFile);
      }

      toast.success('Location updated successfully');
      setEditDialogOpen(false);
      setEditingLocation(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update location');
    } finally {
      setSubmitting(false);
    }
  };

  const canAddLocation = () => {
    // No location limits - always allow adding locations
    return restaurantGroup !== null;
  };

  const formatDate = (dateString) => {
    if (!dateString) {
      // Fallback: calculate from restaurant group created_at
      if (restaurantGroup?.created_at) {
        const createdAt = new Date(restaurantGroup.created_at);
        const trialEnd = new Date(createdAt.getTime() + (30 * 24 * 60 * 60 * 1000));
        return trialEnd.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        });
      }
      return null;
    }
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getDaysRemaining = (dateString) => {
    // If we have a trial_ends_at date, use it
    if (dateString) {
      const endDate = new Date(dateString);
      const today = new Date();
      const diffTime = endDate - today;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return Math.max(0, diffDays);
    }
    // Fallback: calculate from restaurant group created_at (30 day trial)
    if (restaurantGroup?.created_at) {
      const createdAt = new Date(restaurantGroup.created_at);
      const trialEnd = new Date(createdAt.getTime() + (30 * 24 * 60 * 60 * 1000));
      const today = new Date();
      const diffTime = trialEnd - today;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return Math.max(0, diffDays);
    }
    return 30; // Default to 30 days if no data available
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      </Layout>
    );
  }

  const currentPlan = PLANS[restaurantGroup?.plan_type] || PLANS.single;
  const trialDaysRemaining = getDaysRemaining(
    subscriptionStatus?.trial_ends_at ||
    restaurantGroup?.trial_expiry ||
    restaurantGroup?.trial_ends_at
  );

  return (
    <Layout>
      <div className="space-y-6">
        {/* Page header */}
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
            Settings
          </h1>
          <p className="text-slate-500 mt-1">
            Manage your business and subscription
          </p>
        </div>

        {/* 1. Billing Overview + Business Group (data-settings-section="billing" inside) */}
        <BillingSettings
          restaurantGroup={restaurantGroup}
          subscriptionStatus={subscriptionStatus}
          PLANS={PLANS}
          currentPlan={currentPlan}
          trialDaysRemaining={trialDaysRemaining}
          submitting={submitting}
          handleChangeBillingCycle={handleChangeBillingCycle}
          formatDate={formatDate}
        />

        {/* 2. Business Settings — owner + super_admin (data-settings-section="business" inside) */}
        {['owner', 'super_admin'].includes(user?.role) && (
          <BusinessSettings
            reportCurrency={reportCurrency}
            saveCurrency={saveCurrency}
            tenantLogoUrl={tenantLogoUrl}
            setTenantLogoUrl={setTenantLogoUrl}
            saveTenantLogo={saveTenantLogo}
            tenantLogoSaving={tenantLogoSaving}
            brandName={restaurantGroup?.brand_name}
          />
        )}

        {/* 3. Overtime & Holiday Pay — owner + super_admin (data-settings-section="overtime" inside) */}
        {['owner', 'super_admin'].includes(user?.role) && (
          <OvertimeSettings
            payrollSettings={payrollSettings}
            setPayrollSettings={setPayrollSettings}
            payrollSaving={payrollSaving}
            savePayrollSettings={savePayrollSettings}
            addHolidayDate={addHolidayDate}
            removeHolidayDate={removeHolidayDate}
            updateHolidayDate={updateHolidayDate}
            newHolidayIdx={newHolidayIdx}
            newHolidayNameRef={newHolidayNameRef}
          />
        )}

        {/* 4. AI Configuration — owner + super_admin (data-settings-section="ai" inside) */}
        {['owner', 'super_admin'].includes(user?.role) && (
          <AISettings
            aiKeyInput={aiKeyInput}
            setAiKeyInput={setAiKeyInput}
            aiKeyVisible={aiKeyVisible}
            setAiKeyVisible={setAiKeyVisible}
            aiEnabled={aiEnabled}
            setAiEnabled={setAiEnabled}
            aiProvider={aiProvider}
            setAiProvider={setAiProvider}
            aiStatus={aiStatus}
            setAiStatus={setAiStatus}
            aiTestDetail={aiTestDetail}
            aiSaving={aiSaving}
            aiTesting={aiTesting}
            saveAiSettings={saveAiSettings}
            testAiConnection={testAiConnection}
          />
        )}

        {/* 5. Tenant Email Notifications (Resend REST API) — owner, admin, super_admin, manager, supervisor */}
        {/* SendPulse UI is hidden; backend SendPulse infra remains intact for legacy tenants. */}
        {['owner', 'admin', 'super_admin', 'manager', 'supervisor'].includes(user?.role) && (
          <ResendSettings user={user} />
        )}

        {/* 6. Locations — all roles (data-settings-section="locations" inside) */}
        <LocationsSettings
          user={user}
          restaurants={restaurants}
          submitting={submitting}
          setSubmitting={setSubmitting}
          dialogOpen={dialogOpen}
          setDialogOpen={setDialogOpen}
          editDialogOpen={editDialogOpen}
          setEditDialogOpen={setEditDialogOpen}
          editingLocation={editingLocation}
          setEditingLocation={setEditingLocation}
          locationName={locationName}
          setLocationName={setLocationName}
          editForm={editForm}
          setEditForm={setEditForm}
          logoFile={logoFile}
          setLogoFile={setLogoFile}
          logoPreview={logoPreview}
          setLogoPreview={setLogoPreview}
          iconFile={iconFile}
          setIconFile={setIconFile}
          iconPreview={iconPreview}
          setIconPreview={setIconPreview}
          fileInputRef={fileInputRef}
          iconInputRef={iconInputRef}
          deletingLocation={deletingLocation}
          setDeletingLocation={setDeletingLocation}
          deletePinInput={deletePinInput}
          setDeletePinInput={setDeletePinInput}
          deletePinError={deletePinError}
          setDeletePinError={setDeletePinError}
          deleteDialogOpen={deleteDialogOpen}
          setDeleteDialogOpen={setDeleteDialogOpen}
          handleAddLocation={handleAddLocation}
          openEditDialog={openEditDialog}
          handleEditLocation={handleEditLocation}
          handleDeleteLogo={handleDeleteLogo}
          handleDeleteIcon={handleDeleteIcon}
          handleLogoSelect={handleLogoSelect}
          handleIconSelect={handleIconSelect}
          copyLocationPin={copyLocationPin}
          handleDeleteLocation={handleDeleteLocation}
        />

        {/* 6. Upgrade Plan — single plan only */}
        <UpgradeSettings
          restaurantGroup={restaurantGroup}
          subscriptionStatus={subscriptionStatus}
          PLANS={PLANS}
          upgradeDialogOpen={upgradeDialogOpen}
          setUpgradeDialogOpen={setUpgradeDialogOpen}
          handleUpgrade={handleUpgrade}
          submitting={submitting}
        />

        {/* Canadian Payroll — only visible to CA tenants (component self-gates) */}
        <div data-settings-section="canadian_payroll">
          <CanadianEmployerInfoSection />
        </div>

        {/* AI Connections — owner/manager only (component self-gates via API 403) */}
        {(user?.role === 'owner' || user?.role === 'manager' || user?.role === 'super_admin') && (
          <MCPConnectionsSettings />
        )}

        {/* 7. Payment Admin — super_admin only */}
        {user?.role === 'super_admin' && (
          <PaymentAdminSettings
            stripeConfig={stripeConfig}
            setStripeConfig={setStripeConfig}
            stripeMasked={stripeMasked}
            stripeSaving={stripeSaving}
            saveStripeConfig={saveStripeConfig}
            xenditConfig={xenditConfig}
            setXenditConfig={setXenditConfig}
            xenditMasked={xenditMasked}
            xenditSaving={xenditSaving}
            saveXenditConfig={saveXenditConfig}
            activeProcessor={activeProcessor}
            processorSaving={processorSaving}
            processorSwitchBanner={processorSwitchBanner}
            setProcessorSwitchBanner={setProcessorSwitchBanner}
            switchPaymentProcessor={switchPaymentProcessor}
          />
        )}
      </div>
    </Layout>
  );
}
